<template>
  <div
    ref="videoContainer"
    class="preview-video"
    :class="{ 'full-screen': fullScreen }"
  >
    <AttachmentPreviewHeader
      v-if="fullScreen"
      :title="attachment.name"
      @downloadAttachment="downloadAttachment"
      @closePreview="closePreview"
      @deleteAttachment="deleteAttachment"
      @copyLink="copyLink"
    />
    <video
      :key="attachmentUrl"
      ref="video"
      :class="videoClass"
      :loop="loop"
      class="video-player"
      @timeupdate="handleTimeUpdate"
      @loadedmetadata="setDurationAndDimensions"
      @click="playVideo"
      @ended="handleEnd"
      @loadeddata="videoLoaded"
    >
      <source :src="attachmentUrl" />
    </video>
    <LoadingSpinner
      v-show="!loaded"
      class="video-loading-spinner"
    />
    <div
      v-show="loaded"
      ref="controls"
      class="controls"
      :class="{ paused: !playing }"
    >
      <div class="controls-top">
        <div class="controls-top-left">
          <em
            :style="getCustomIconStyle(iconPath(playPauseIcon))"
            class="custom-icons attachment-preview-icons pt usn"
            @click="playVideo"
          />
          <span class="duration-text usn">{{ currentTime }} / {{ duration }}</span>
        </div>
        <div class="controls-top-right">
          <div class="volume-control">
            <input
              ref="volumeRange"
              class="volume-range pt"
              type="range"
              :min="0"
              :max="1"
              :step="0.01"
              @input="inputRangeHandler"
              @change="changeRangeHandler"
              @mousedown="mouseDownHandler"
            />
            <em
              :style="getCustomIconStyle(iconPath(volumeIcon))"
              class="custom-icons attachment-preview-icons pt usn"
              @click="mute"
            />
          </div>
          <em
            :style="getCustomIconStyle(iconPath(fullScreenIcon))"
            class="custom-icons attachment-preview-icons full-screen-icon pt usn"
            @click="handleFullScreen"
          />
        </div>
      </div>
      <div class="controls-bottom">
        <ProgressBar
          class="pt"
          :percentage-data="[{ percentage: progressValue, color: '#ffffff' }]"
          :tooltip="false"
          :thumb="true"
          @seek="handleSeek"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ProgressBar from '@/components/elements/ProgressBar';
import customIconsMixin from '@/mixins/customIconsMixin';
import LoadingSpinner from '@/components/elements/LoadingSpinner';
import AttachmentPreviewHeader from './AttachmentPreviewHeader';
import {
  browserStorageGetItem,
  browserStorageSetItem,
  browserStorageRemoveItem,
} from '@/utilities/browser-storage.util';

export default {
  name: 'VideoAttachmentPreview',
  components: {
    ProgressBar,
    AttachmentPreviewHeader,
    LoadingSpinner,
  },
  mixins: [customIconsMixin],
  props: {
    attachment: {
      type: Object,
      required: true,
    },
    attachmentUrl: {
      type: String,
      required: true,
    },
    loop: {
      type: Boolean,
      default: false,
    },
    forceAutoplay: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['exitFullScreen', 'downloadAttachment', 'closePreview', 'deleteAttachment', 'copyLink'],
  data() {
    return {
      player: null,
      duration: '',
      currentTime: '00:00',
      progressValue: 0,
      playPauseIcon: 'play',
      fullScreen: false,
      vertical: false,
      progressInterval: null,
      intervalDuration: 30,
      volumeIcon: 'volume-2',
      muted: false,
      playing: false,
      loaded: false,
      inputRangeVolume: 1,
    };
  },
  computed: {
    videoClass() {
      return this.vertical ? 'v-vertical' : 'v-horizontal';
    },

    fullScreenIcon() {
      return this.fullScreen ? 'minimize-fullscreen' : 'maximize';
    },
  },
  mounted() {
    this.player = this.$refs.video;
    this.setDefaultVolume();
    this.addEventListeners();
  },
  activated() {
    this.addEventListeners();
  },
  deactivated() {
    this.removeEventListeners();
  },
  unmounted() {
    clearInterval(this.progressInterval);
    this.removeEventListeners();
  },
  methods: {
    playVideo() {
      if (this.player.paused || this.player.ended) {
        this.player.play();
        this.playing = true;
        this.playPauseIcon = 'pause';
        const videoDuration = this.player.duration * 1000;
        const intervalCount = Math.ceil(videoDuration / this.intervalDuration);
        const value = 100 / intervalCount;

        this.progressInterval = setInterval(() => {
          if (this.loop && this.progressValue >= 100) {
            this.progressValue = 0;
          } else {
            this.progressValue += value;
          }
        }, this.intervalDuration);
      } else {
        this.player.pause();
        this.playing = false;
        clearInterval(this.progressInterval);
        this.playPauseIcon = 'play';
      }
    },
    setDurationAndDimensions() {
      this.duration = this.convertSeconds(this.player.duration);

      // set dimensions
      this.player.height = this.player.videoHeight;
      this.player.width = this.player.videoWidth;
    },
    convertSeconds(seconds) {
      const date = new Date(null);
      date.setSeconds(seconds);
      if (seconds >= 3600) {
        this.currentTime = '00:00:00';
        return date.toISOString().substr(11, 8);
      }
      return date.toISOString().substr(14, 5);
    },
    handleTimeUpdate() {
      this.currentTime = this.convertSeconds(this.player.currentTime);
    },
    handleEnd() {
      this.playPauseIcon = 'play';
      this.progressValue = 0;
      this.playing = false;
      clearInterval(this.progressInterval);
    },
    handleFullScreen() {
      if (this.$refs.videoContainer.requestFullscreen) {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        } else {
          this.$refs.videoContainer.requestFullscreen();
        }
      }
    },
    isVerticalVideo() {
      const { videoHeight, videoWidth } = this.$refs.video;
      return videoHeight > videoWidth;
    },
    videoLoaded() {
      if (this.$refs.video && this.$refs.video.readyState >= 2) {
        this.vertical = this.isVerticalVideo();
        if (this.forceAutoplay || !this.$store.getters.isPreviousRouteRoot) {
          this.playVideo();
        }
        this.loaded = true;
      }
    },
    closeFullScreen() {
      this.fullScreen = false;
    },
    handleFullScreenChanged() {
      this.fullScreen = !this.fullScreen;
      if (!this.fullScreen) {
        this.$emit('exitFullScreen');
      }
    },
    mouseDownHandler() {
      this.inputRangeVolume = this.player.volume;
    },
    inputRangeHandler($event) {
      this.setInputRangeData($event.target.value);
    },
    setInputRangeData(rangeValue) {
      this.player.volume = rangeValue;
      const value = rangeValue * 100;
      const white = 'rgba(255, 255, 255, 0.25)';
      this.$refs.volumeRange.style.background = `linear-gradient(to right, #fff 0%, #fff ${value}%, ${white} ${value}%, ${white} 100%)`;
      this.setVolumeIcon(value);
    },
    setVideoVolumeToStorage(value) {
      browserStorageSetItem(this.$constants.videoVolumeStorageKey, value);
    },
    changeRangeHandler($event) {
      const value = +$event.target.value;
      if (value === 0) {
        browserStorageSetItem(this.$constants.videoMuteVolumeKey, this.inputRangeVolume);
      } else {
        browserStorageRemoveItem(this.$constants.videoMuteVolumeKey);
      }
      this.setVideoVolumeToStorage(value);
    },
    setDefaultVolume() {
      let volume = this.getStoredVolume(this.$constants.videoVolumeStorageKey);
      if (volume < 0 || volume > 1) {
        browserStorageRemoveItem(this.$constants.videoVolumeStorageKey);
        volume = this.$constants.videoVolumeDefault;
      }
      this.setInputRangeData(volume);
      if (!this.$refs.volumeRange) return;
      this.$refs.volumeRange.value = volume;
    },
    getStoredVolume(key) {
      const storedValue = parseFloat(browserStorageGetItem(key));
      return !isNaN(storedValue) ? storedValue : this.$constants.videoVolumeDefault;
    },
    setVolumeIcon(value) {
      if (value === 0) {
        this.setMuteIcon();
        this.muted = true;
        return;
      }
      if (value < 50) {
        this.volumeIcon = 'volume-1';
        this.muted = false;
        return;
      }
      this.volumeIcon = 'volume-2';
      this.muted = false;
    },
    handleSeek(percent) {
      const value = (this.player.duration / 100) * percent;
      this.player.currentTime = value;
      this.progressValue = percent;
    },
    setMuteIcon() {
      this.volumeIcon = 'volume-x';
    },
    mute() {
      const value = this.muted ? this.getStoredVolume(this.$constants.videoMuteVolumeKey) : 0;
      if (!this.muted) {
        browserStorageSetItem(this.$constants.videoMuteVolumeKey, this.player.volume);
      } else {
        browserStorageRemoveItem(this.$constants.videoMuteVolumeKey);
      }
      this.setVideoVolumeToStorage(value);
      this.setInputRangeData(value);
      this.$refs.volumeRange.value = value;
    },
    checkKey(e) {
      if (e.keyCode === 32 || e.key === 'Space') {
        this.playVideo();
      }
    },
    downloadAttachment() {
      this.$emit('downloadAttachment');
    },
    closePreview() {
      this.$emit('closePreview');
    },
    deleteAttachment() {
      this.$emit('deleteAttachment');
    },
    copyLink() {
      this.$emit('copyLink');
    },

    addEventListeners() {
      if (this.listenersAdded) return;
      document.addEventListener('fullscreenchange', this.handleFullScreenChanged);
      window.addEventListener('keypress', this.checkKey);
      this.listenersAdded = true;
    },

    removeEventListeners() {
      if (!this.listenersAdded) return;
      document.removeEventListener('fullscreenchange', this.handleFullScreenChanged);
      window.removeEventListener('keypress', this.checkKey);
      this.listenersAdded = false;
    },
  },
};
</script>

<style lang="scss">
.preview-video {
  position: relative;
  max-height: calc(100% - (2 * #{$headerFooterHeight}));
  z-index: 0;
  display: flex;
  flex-grow: 1;
  min-width: 0;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  max-width: fit-content;
  justify-content: center;
  align-items: center;

  .video-loading-spinner {
    position: absolute;
    width: 62px;
    height: 62px;
  }

  input[type='range'] {
    -webkit-appearance: none;
    background: linear-gradient(to right, #fff 0%, #fff 100%);
    height: 4px;
    border-radius: 8px;

    // thumb style
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      height: 10px;
      width: 10px;
      border-radius: 50%;
      position: relative;
      bottom: 3px;
      background: #bec0c0;
    }
    &::-moz-range-thumb {
      height: 10px;
      width: 10px;
      border-radius: 50%;
      position: relative;
      bottom: 3px;
      background: #bec0c0;
    }
    &::-ms-thumb {
      height: 10px;
      width: 10px;
      border-radius: 50%;
      position: relative;
      bottom: 3px;
      background: #bec0c0;
    }

    // truck style
    &::-webkit-slider-runnable-track {
      border-radius: 8px;
      -webkit-appearance: none;
      height: 4px;
    }
    &::-moz-range-track {
      -webkit-appearance: none;
      border-radius: 8px !important;
      height: 4px !important;
    }
    &:focus::-moz-range-track {
      border-radius: 8px !important;
      height: 4px !important;
    }
  }

  video {
    min-height: 100%;
    width: 100%;
    height: 100%;
  }

  &.full-screen {
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: unset !important;
    height: 100vh;
    width: 100vw;

    video {
      width: 100vw;
      height: auto;
      max-height: unset;
      max-width: unset;

      &.v-vertical {
        height: 100vh !important;
        width: auto !important;
        max-height: unset;
        max-width: unset;
      }

      &.v-horizontal {
        width: 100vw;
        height: auto;
      }
    }
  }

  video::-webkit-media-controls-enclosure {
    display: none !important;
  }

  .vidBar {
    z-index: 2147483648;
  }

  .controls {
    transform: translateZ(0px);
    z-index: 100;
    height: 71px;
    box-sizing: border-box;
    width: 100%;
    position: absolute;
    bottom: 0px;
    display: flex;
    flex-direction: column;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 4.94%, rgba(0, 0, 0, 0.595) 64.66%);
    padding: 22px 24px;
    opacity: 0;
    transition: opacity 0.6s linear;

    &:hover {
      opacity: 1;
    }

    &.paused {
      opacity: 1;
    }
    .controls-top {
      color: #ffffff;
      display: flex;
      justify-content: space-between;

      .controls-top-left,
      .controls-top-right {
        display: flex;
        justify-content: center;
        align-items: center;
      }

      .controls-top-right {
        .full-screen-icon {
          margin-left: 24px;
        }

        .volume-control {
          @apply flex items-center justify-end;

          input[type='range'] {
            // thumb style
            &::-webkit-slider-thumb {
              visibility: hidden;
            }
            &::-moz-range-thumb {
              visibility: hidden;
            }
            &::-ms-thumb {
              visibility: hidden;
            }
          }

          .volume-range {
            max-width: 0px;
            transition: max-width 0.3s ease-in-out;
          }

          &:hover {
            input[type='range'] {
              &::-webkit-slider-thumb {
                visibility: visible;
                background-color: #bec0c0;
              }
              &::-moz-range-thumb {
                visibility: visible;
              }
              &::-ms-thumb {
                visibility: visible;
              }
            }
            .volume-range {
              display: unset;
              max-width: 69px;
            }
          }

          em {
            margin-left: 24px;
          }
        }
      }

      .controls-top-left {
        .duration-text {
          margin-left: 16px;
          font-size: 14px;
          line-height: 16px;
          letter-spacing: 0.3px;
          color: $stLightSilver;
        }
      }
    }

    .controls-bottom {
      width: 100%;
      margin-top: 10px;

      .progress-bar {
        background-color: rgba(255, 255, 255, 0.25);
        transition: all 0.4s linear;

        .cl-progress-wrap {
          background-color: rgba(255, 255, 255, 0.25);

          .cl-progress {
            will-change: width;
            transition: unset;
            position: relative;
            display: flex;
            align-items: center;
          }
        }
      }
    }
  }
}
</style>
